import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Image, Item, Icon, Button, Segment, Label, Dropdown, Message, Dimmer, Loader, Header, Table, Input } from 'semantic-ui-react';
import characterImage from '../../assets/shutterstock_2294275225-480x480.jpg';
import providerImage from '../../assets/shutterstock_2294366549-480x480.png';
import { useSelector, useDispatch } from 'react-redux';
import { setTargetData } from '../../slices/aaiRecordingUtilitySlice';
import { setTargetObj } from '../../slices/aaiCharSequencerSlice';
import RecordingControls from "../../utils/aaiRecordingControls";
import { FaTimes } from 'react-icons/fa';
import { selectActiveDetailObj } from '../../slices/userLitGroupAssetsSlice';
import CharacterItemCard from '../../components/characterDisplay/CharacterItemCard';
import "../../styles.css";
// Import selectors from voiceActorSlice
import {
  selectPortfoliosWithCharacters
} from '../../slices/voiceActorSlice';

// Import the Voice Lab composite data hook
import useVoiceLabCompositeData from '../../hooks/useVoiceLabCompositeData';

// Import voice actor hooks
import { useVoiceActorUtilities } from '../../hooks/voiceActor';

// Custom RecorderDialog component that works directly within the Voice Lab page
const InlineRecorderDialog = ({ targetData, voiceDetailObj, isOpen, handlePopupClose }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 }); // Initial position
  const popupRef = useRef(null);
  const startPosition = useRef({ x: 0, y: 0 });

  const {
    character: {
      character: { name } = { name: "Default Name" }
    } = {},
    variation = "Default Variation",
  } = targetData?.character || {};

  if (!isOpen) return null; // Hide the dialog if not open

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startPosition.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - startPosition.current.x,
      y: e.clientY - startPosition.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div
      ref={popupRef}
      style={{
        position: "fixed", // Changed from absolute to fixed to ensure it's visible
        top: position.y,
        left: position.x,
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        width: "400px",
        zIndex: 2200,
        backgroundColor: "white",
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp} // Stop dragging if the mouse leaves the popup
    >
      <div style={{ marginBottom: "10px", width: "100%", height: "30px", borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#ececec", position: "absolute", top: "0px", left: "0px" }}>
        <Button
          circular
          icon
          className="closeButton"
          style={{ marginTop: "-8px", position: "absolute", top: "8px", right: "8px" }}
          onClick={handlePopupClose} // Close the popup
        >
          <FaTimes className="closeIcon" />
        </Button>
        <div className="fullHeaderFont" style={{ margin: 0, textAlign: "center" }}>
          Record Variation
        </div>
      </div>
      <div style={{ marginTop: "15px", textAlign: "center" }}>
        <span className="subHeadingFont">{name}</span> - <span className="subHeadingFont">{variation}</span>
      </div>

      {/* Content Section */}
      <div style={{ padding: "5px 0px 0px 0px" }}>
        <Grid style={{ marginBottom: "0px" }}>
          <Grid.Row>
            <Grid.Column className="noMargNoPad">
              <RecordingControls voiceDetailObj={voiceDetailObj} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </div>
  );
};

// Variation Card Component
const VariationCard = ({ variation, character, onPlay, onRecord, isPlaying }) => {
  return (
    <Segment raised style={{ marginBottom: '10px' }}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={12}>
            <Header as="h4">{variation.name}</Header>
            <Label color={variation.completed ? 'green' : 'grey'}>
              {variation.completed ? 'Recorded' : 'Not Recorded'}
            </Label>
            {variation.version && (
              <Label>Version {variation.version}</Label>
            )}
          </Grid.Column>
          <Grid.Column width={4} textAlign="right">
            {variation.completed ? (
              <Button
                icon
                labelPosition='left'
                size='small'
                color={isPlaying ? 'red' : 'green'}
                onClick={() => onPlay({
                  uuid: variation.variationId,
                  s3Url: variation.s3Url,
                  characterId: character.uuid
                })}
              >
                <Icon name={isPlaying ? 'pause' : 'play'} />
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
            ) : (
              <Button
                icon
                labelPosition='left'
                size='small'
                color='blue'
                onClick={() => onRecord({
                  uuid: variation.variationId,
                  name: variation.name,
                  content: variation.sample_text,
                  characterName: character.name,
                  characterId: character.uuid
                })}
              >
                <Icon name='microphone' />
                Record
              </Button>
            )}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <div style={{
              padding: '8px',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px',
              fontStyle: 'italic'
            }}>
              "{variation.sample_text}"
            </div>
          </Grid.Column>
        </Grid.Row>
        {variation.completed && variation.s3Url && (
          <Grid.Row>
            <Grid.Column width={16}>
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                <Icon name="file audio" /> {variation.s3Url.split('/').pop()}
              </div>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Segment>
  );
};

// Filterable Variations List Component
const FilterableVariationsList = ({
  character,
  variations,
  onPlay,
  onRecord,
  playingVariationId,
  isPlaying
}) => {
  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('ascending');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'

  // Apply filters and sorting to variations
  const filteredVariations = useMemo(() => {
    let result = [...variations];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(v =>
        v.name.toLowerCase().includes(term) ||
        (v.description && v.description.toLowerCase().includes(term)) ||
        (v.sample_text && v.sample_text.toLowerCase().includes(term))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(v =>
        statusFilter === 'recorded' ? v.completed : !v.completed
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status':
          comparison = (a.completed === b.completed) ? 0 : a.completed ? -1 : 1;
          break;
        case 'version':
          comparison = (a.version || 0) - (b.version || 0);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'ascending' ? comparison : -comparison;
    });

    return result;
  }, [variations, searchTerm, statusFilter, sortBy, sortDirection]);

  // Toggle sort direction when clicking the same column
  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
    } else {
      setSortBy(column);
      setSortDirection('ascending');
    }
  };

  // Render the table view
  const renderTableView = () => (
    <Table celled sortable compact className='variations-table'>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell
            sorted={sortBy === 'name' ? sortDirection : null}
            onClick={() => handleSortChange('name')}
          >
            Name
          </Table.HeaderCell>
          {/* <Table.HeaderCell>Description</Table.HeaderCell> */}
          <Table.HeaderCell>Sample Text</Table.HeaderCell>
          <Table.HeaderCell
            sorted={sortBy === 'status' ? sortDirection : null}
            onClick={() => handleSortChange('status')}
          >
            Status
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={sortBy === 'version' ? sortDirection : null}
            onClick={() => handleSortChange('version')}
          >
            Ver
          </Table.HeaderCell>
          <Table.HeaderCell>Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {filteredVariations.map(variation => (
          <Table.Row key={variation.variationId}>
            <Table.Cell>
              <div style={{
                // maxHeight: '60px',
                maxWidth: '80px',  // Set your desired max width
                overflow: 'hidden', // Changed from 'auto' to 'hidden'
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' // For single line ellipsis
              }}>
                {variation.name}
              </div>

            </Table.Cell>
            {/* <Table.Cell>{variation.description}</Table.Cell> */}
            <Table.Cell>
              <div style={{
                maxHeight: '60px',
                maxWidth: '100px',  // Set your desired max width
                overflow: 'hidden', // Changed from 'auto' to 'hidden'
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' // For single line ellipsis
              }}>
                {variation.sample_text}
              </div>
            </Table.Cell>
            <Table.Cell>
              <Label color={variation.completed ? 'green' : 'grey'}>
                {variation.completed ? 'Recorded' : 'Not Recorded'}
              </Label>
            </Table.Cell>
            <Table.Cell>{variation.version || 1}</Table.Cell>
            <Table.Cell>
              {variation.completed ? (
                <Button
                  icon
                  size='small'
                  color={isPlaying && playingVariationId === variation.variationId ? 'red' : 'green'}
                  onClick={() => onPlay({
                    uuid: variation.variationId,
                    s3Url: variation.s3Url,
                    characterId: character.uuid
                  })}
                >
                  <Icon name={isPlaying && playingVariationId === variation.variationId ? 'pause' : 'play'} />
                </Button>
              ) : (
                <Button
                  icon
                  size='small'
                  color='blue'
                  onClick={() => onRecord({
                    uuid: variation.variationId,
                    name: variation.name,
                    content: variation.sample_text,
                    characterName: character.name,
                    characterId: character.uuid
                  })}
                >
                  <Icon name='microphone' />
                </Button>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  // Render the card view
  const renderCardView = () => (
    <div>
      {filteredVariations.map(variation => (
        <VariationCard
          key={variation.variationId}
          variation={variation}
          character={character}
          onPlay={onPlay}
          onRecord={onRecord}
          isPlaying={isPlaying && playingVariationId === variation.variationId}
        />
      ))}
    </div>
  );

  return (
    <div className="filterable-variations-list">
      {/* Filter Controls */}
      <div className="filter-controls" style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <Input
              icon='search'
              placeholder='Search variations...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: '10px', width: '200px' }}
            />
            <Dropdown
              text={`Status: ${statusFilter === 'all' ? 'All' : statusFilter === 'recorded' ? 'Recorded' : 'Not Recorded'}`}
              inline
              options={[
                { key: 'all', text: 'All', value: 'all' },
                { key: 'recorded', text: 'Recorded', value: 'recorded' },
                { key: 'not-recorded', text: 'Not Recorded', value: 'not-recorded' }
              ]}
              value={statusFilter}
              onChange={(e, { value }) => setStatusFilter(value)}
            />
          </div>
          <div>
            <Button.Group size='tiny'>
              <Button
                icon='table'
                active={viewMode === 'table'}
                onClick={() => setViewMode('table')}
              />
              <Button
                icon='block layout'
                active={viewMode === 'card'}
                onClick={() => setViewMode('card')}
              />
            </Button.Group>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Label>
              <Icon name="list" />
              {filteredVariations.length} of {variations.length} Variations
            </Label>
            <Label color="green">
              <Icon name="check" />
              {variations.filter(v => v.completed).length} Recorded
            </Label>
          </div>
        </div>
      </div>

      {/* Variations List */}
      {filteredVariations.length > 0 ? (
        viewMode === 'table' ? renderTableView() : renderCardView()
      ) : (
        <Message info size="small">
          <Message.Header>No Variations Found</Message.Header>
          <p>No variations match your current filters. Try adjusting your search criteria.</p>
        </Message>
      )}
    </div>
  );
};

// Portfolio Recording Item Component
const PortfolioRecordingItem = (props) => {
  const { portfolio, characters, index } = props;

  // Debug logs for portfolio and characters
  console.log('Portfolio:', portfolio);
  console.log('Characters in portfolio:', characters);
  console.log('VariationsMap:', props.variationsMap);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [targetData, setLocalTargetData] = useState(null);
  const [playingVariationId, setPlayingVariationId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || 'default-user-id';
  const voiceDetailObj = useSelector(selectActiveDetailObj);
  // Get all characters from the composite data hook
  const { characters: allCharacters } = useVoiceLabCompositeData();

  // Add voice actor hooks
  const { getSignedUrl } = useVoiceActorUtilities();

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayingVariationId(null);
  };

  // Map portfolio data to UI format
  const header = portfolio.name;
  const description = portfolio.description || "No description available";
  const client = portfolio.client || "Personal Portfolio";
  const created_date = new Date(portfolio.createdAt).toLocaleDateString();
  const updated_date = new Date(portfolio.updatedAt).toLocaleDateString();
  const imgSrc = characterImage; // Default image

  // Use useState to generate random values only once when the component mounts
  const [usage] = useState(() => portfolio.usage || ["Audiobook Series", "Video Game", "Animation Series", "Documentary Series"][Math.floor(Math.random() * 4)]);
  const [status] = useState(() => portfolio.status || ["Active", "In Development", "Completed"][Math.floor(Math.random() * 3)]);

  const handlePlayClick = async (variation) => {
    // If this variation is already playing, pause it
    if (playingVariationId === variation.uuid && isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      return;
    }

    // If the variation has an audio file, play it
    if (variation.s3Url) {
      try {
        // Get a signed URL for playback
        const signedUrl = await getSignedUrl(variation.s3Url, userId);

        // Set the audio source and play it
        if (audioRef.current) {
          audioRef.current.src = signedUrl;
          audioRef.current.load();
          audioRef.current.play();
          setIsPlaying(true);
          setPlayingVariationId(variation.uuid);
        }
      } catch (error) {
        console.error('Error getting signed URL:', error);
      }
    } else {
      console.warn('Variation has no audio file:', variation);
    }
  };

  const handleRecordClick = (variation) => {
    // Find the character this variation belongs to
    const character = characters.find(c => c.uuid === variation.characterId);

    // Create target data for the recording dialog
    const newTargetData = {
      character: {
        character: {
          name: character ? character.name : "Unknown Character",
          uuid: variation.characterId
        },
        variation: variation.name,
        textSnippet: {
          content: variation.content || "No sample text available"
        }
      }
    };

    // Set local state for the dialog
    setLocalTargetData(newTargetData);

    // Also dispatch to Redux for other components that might need this data
    dispatch(setTargetData(newTargetData));
    dispatch(setTargetObj(newTargetData));

    // Open the dialog locally
    setIsDialogOpen(true);
  };

  const handlePopupClose = () => {
    setIsDialogOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'green';
      case 'In Development': return 'orange';
      case 'Completed': return 'blue';
      default: return 'grey';
    }
  };

  return (
    <Item key={index.toString()} >
      <Item.Content >
        {/* Hidden audio element for direct playback */}
        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          style={{ display: "none" }}
        />

        {/* Header and Description Section with Image */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', backgroundColor: "#ececec", padding: "8px", borderRadius: "8px" }}>
          <Image size='tiny' src={imgSrc} />
          <div style={{ marginLeft: '15px' }}>
            <Item.Header as='a' style={{ width: "100%", fontSize: "1.3rem" }}>
              {header}
            </Item.Header>
            <Item.Meta>{client}</Item.Meta>
            <Item.Description>
              <p style={{ maxWidth: "600px" }}>{description}</p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '130px 110px 120px',
                gap: '10px',
                marginTop: '5px'
              }}>
                <Label>
                  <Icon name='film' />
                  Usage: {usage}
                </Label>
                <Label color={getStatusColor(status)}>
                  <Icon name='sync' />
                  Status: {status}
                </Label>
                <Label>
                  <Icon name='user' />
                  Characters: {characters.length}
                </Label>
              </div>
            </Item.Description>
            <Item.Extra>
              created: <span>{created_date}</span>
              <span className="listUpdatedText">updated: {updated_date}</span>
            </Item.Extra>
          </div>
        </div>

        {/* Characters Section */}
        <Segment basic>
          <div className="fullHeaderFont" style={{ marginBottom: '10px' }}>Characters:</div>
          {characters.length > 0 ? (
            characters.map((character, characterIndex) => {
              // Get the character data with variations from allCharacters array
              const fullChar = allCharacters.find(c => c.uuid === character.uuid);

              // Get variations for this character from the parent component's variationsMap
              const variations = props.variationsMap[character.uuid] || character.variations || [];

              // Log the character and its variations for debugging
              console.log(`DEBUG: Character ${character.name} - Full data:`, !!fullChar,
                `Variations count: ${variations.length}`);

              // Map variations to the format expected by FilterableVariationsList
              const emotions = variations.map(variation => ({
                name: variation.name,
                description: variation.content || "No description available",
                sample_text: variation.content || "No sample text available",
                completed: !!variation.s3Url,
                variationId: variation.uuid,
                s3Url: variation.s3Url || '',
                version: variation.version || 1
              }));

              // Render variations for this character using the new filterable component
              const renderVariations = () => (
                <FilterableVariationsList
                  character={character}
                  variations={emotions}
                  onPlay={handlePlayClick}
                  onRecord={handleRecordClick}
                  playingVariationId={playingVariationId}
                  isPlaying={isPlaying}
                />
              );

              return (
                <Segment key={characterIndex} style={{ marginBottom: '20px' }}>
                  <CharacterItemCard
                    character={fullChar || character} // Use full character if available
                    userId={userId}
                    renderVariations={renderVariations}
                  />
                </Segment>
              );
            })
          ) : (
            <Message info>
              <Message.Header>No Characters Found</Message.Header>
              <p>This portfolio doesn't have any characters yet. Add characters in the Voice Actor Worksheet.</p>
            </Message>
          )}
        </Segment>
      </Item.Content>

      {/* Inline recorder dialog */}
      <InlineRecorderDialog
        targetData={targetData}
        voiceDetailObj={voiceDetailObj}
        isOpen={isDialogOpen}
        handlePopupClose={handlePopupClose}
      />
    </Item>
  );
};

// Provider Profile Component
const ProviderProfile = ({ characters, variationsMap }) => {
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.user.profile);

  // Get all variations from the variationsMap
  const allVariations = Object.values(variationsMap || {}).flat();

  // Calculate stats from real data
  const providerStats = {
    totalRecordings: allVariations.filter(v => v.s3Url).length,
    pendingRequests: (characters || []).length - (characters || []).filter(c => c.status === 'Completed').length,
    completedThisWeek: allVariations.filter(v => {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return v.s3Url && new Date(v.updatedAt) > oneWeekAgo;
    }).length,
    specialties: userProfile?.data?.specialties || ['Audiobooks', 'Animation', 'Character Voices']
  };

  // Handle image loading errors
  const handleImageError = (e) => {
    console.warn('Provider image failed to load');
    e.target.src = providerImage; // Use default image as fallback
  };

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#f8f8f8',
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={3}>
            <Image
              src={userProfile?.data?.avatarUrl || providerImage}
              size='small'
              circular
              onError={handleImageError}
            />
          </Grid.Column>
          <Grid.Column width={9} verticalAlign='middle'>
            <div className="fullHeaderFont">Voice Actor {userProfile?.data?.title ? `- ${userProfile.data.title}` : ''}</div>
            <div style={{ marginTop: '10px' }}>
              <Label>
                <Icon name='microphone' />
                {providerStats.totalRecordings} Recordings
              </Label>
              <Label>
                <Icon name='clock' />
                {providerStats.pendingRequests} Pending
              </Label>
              <Label>
                <Icon name='check' />
                {providerStats.completedThisWeek} This Week
              </Label>
            </div>
            <div style={{ marginTop: '10px' }}>
              <strong>Specialties: </strong>
              {providerStats.specialties.join(', ')}
            </div>
          </Grid.Column>
          <Grid.Column width={4} verticalAlign='middle' textAlign='right'>
            <Button color='green'>
              <Icon name='setting' />
              Profile Settings
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <div style={{ marginTop: '15px', textAlign: 'center' }}>
        <Button
          color='blue'
          size='large'
          onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-LAB' } })}
        >
          <Icon name='microphone' />
          Authorator-ai Requests
        </Button>
      </div>
    </div>
  );
};

// Main Component
const VoiceLabMyRecordings = () => {
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.user.profile);

  // Use the Voice Lab composite data hook to fetch and manage data
  const {
    actorUsers,
    portfolios,
    characters,
    variationsMap,
    isLoading,
    error
  } = useVoiceLabCompositeData();

  // State for filtered portfolios
  const [filteredPortfolios, setFilteredPortfolios] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [usageFilter, setUsageFilter] = useState('all');
  const [voiceActorFilter, setVoiceActorFilter] = useState('all');
  const [characterFilter, setCharacterFilter] = useState('all');

  // Log data status
  console.log('Voice Lab data status:', {
    actorUsersCount: actorUsers.length,
    portfoliosCount: portfolios.length,
    charactersCount: characters.length,
    isLoading,
    hasError: !!error
  });

  // Use the enhanced portfolios with characters selector
  const portfolioData = useSelector(selectPortfoliosWithCharacters);

  // Debug log to see what's coming from the selector
  console.log('Portfolio data from selector:', portfolioData);

  // If portfolioData is empty but we have portfolios and characters, create a fallback
  const [fallbackPortfolioData, setFallbackPortfolioData] = useState([]);

  useEffect(() => {
    // Only create fallback if portfolioData is empty but we have portfolios and characters
    if ((!portfolioData || portfolioData.length === 0) &&
      portfolios && portfolios.length > 0 &&
      characters && characters.length > 0) {

      console.log('Creating fallback portfolio data');

      // Create a map of portfolioId to characters
      const portfolioCharactersMap = {};
      characters.forEach(character => {
        if (character.portfolioId) {
          if (!portfolioCharactersMap[character.portfolioId]) {
            portfolioCharactersMap[character.portfolioId] = [];
          }
          portfolioCharactersMap[character.portfolioId].push({
            ...character,
            variations: variationsMap[character.uuid] || []
          });
        }
      });

      // Create fallback portfolio data
      const fallback = portfolios.map(portfolio => ({
        ...portfolio,
        characters: portfolioCharactersMap[portfolio.uuid] || [],
        variationCount: (portfolioCharactersMap[portfolio.uuid] || []).reduce(
          (count, character) => count + (variationsMap[character.uuid]?.length || 0),
          0
        )
      }));

      setFallbackPortfolioData(fallback);
      console.log('Fallback portfolio data created:', fallback);
    }
  }, [portfolioData, portfolios, characters, variationsMap]);

  // Use fallback data if portfolioData is empty
  const effectivePortfolioData = portfolioData && portfolioData.length > 0
    ? portfolioData
    : fallbackPortfolioData;

  // Debug log to show the characters structure
  useEffect(() => {
    if (characters && characters.length > 0) {
      console.log('DEBUG: Characters Structure:',
        characters.map(char => ({
          name: char.name,
          uuid: char.uuid,
          variationCount: variationsMap[char.uuid]?.length || 0,
          hasRecordings: variationsMap[char.uuid]?.some(v => v.s3Url) || false
        }))
      );

      // Log the first character's variations in detail if available
      if (characters[0] && variationsMap[characters[0].uuid]?.length > 0) {
        console.log('DEBUG: First Character Variations:', variationsMap[characters[0].uuid]);
      }
    }
  }, [characters, variationsMap]);

  // Debug log for actorUsers, portfolios, and their relationships
  useEffect(() => {
    if (actorUsers && actorUsers.length > 0) {
      // Log the complete actor objects to see all properties
      console.log('DEBUG: Complete Actor Users:', actorUsers);

      // Log a simplified view of actor properties
      console.log('DEBUG: Actor Users Structure:', actorUsers.map(actor => ({
        uuid: actor.uuid,
        id: actor.id,
        _id: actor._id,
        name: actor.name,
        firstName: actor.firstName,
        lastName: actor.lastName,
        displayName: actor.displayName,
        email: actor.email
      })));
    }

    if (portfolios && portfolios.length > 0) {
      // Log the complete portfolio objects to see all properties
      console.log('DEBUG: Complete Portfolios:', portfolios);

      // Log all properties of the first portfolio
      const firstPortfolio = portfolios[0];
      console.log('DEBUG: First Portfolio All Properties:', Object.keys(firstPortfolio).reduce((obj, key) => {
        obj[key] = firstPortfolio[key];
        return obj;
      }, {}));
    }

    if (portfolios && portfolios.length > 0 && actorUsers && actorUsers.length > 0) {
      // Create a map of actor UUIDs to their portfolios
      const actorPortfolioMap = {};

      // For each actor, try to find portfolios that might be associated with them
      actorUsers.forEach(actor => {
        // Try all possible ID properties from the actor
        const actorIds = [
          actor.uuid,
          actor.id,
          actor._id,
          actor.userId,
          actor.user_id
        ].filter(Boolean); // Filter out undefined/null values

        console.log(`DEBUG: Actor ${actor.displayName || actor.name || actor.uuid} possible IDs:`, actorIds);

        // For each portfolio, check all properties that might contain an actor reference
        const actorPortfolios = portfolios.filter(portfolio => {
          // Get all properties of the portfolio
          const portfolioProps = Object.entries(portfolio);

          // Check if any actor ID matches any portfolio property
          return portfolioProps.some(([key, value]) => {
            const isMatch = actorIds.includes(value);
            if (isMatch) {
              console.log(`DEBUG: Found match! Actor ID ${value} matches portfolio.${key}`);
            }
            return isMatch;
          });
        });

        console.log(`DEBUG: Portfolios for actor ${actor.displayName || actor.name || actor.uuid}:`,
          actorPortfolios.map(p => ({ uuid: p.uuid, name: p.name })));

        // Store the count of portfolios, characters, and variations for this actor
        actorPortfolioMap[actor.uuid] = {
          actorName: actor.displayName || actor.name || `Actor ${actor.uuid.substring(0, 8)}`,
          portfolioCount: actorPortfolios.length,
          portfolios: actorPortfolios.map(p => ({
            uuid: p.uuid,
            name: p.name,
            characterCount: (p.characters || []).length,
            variationCount: (p.characters || []).reduce((count, char) => {
              return count + (variationsMap[char.uuid]?.length || 0);
            }, 0)
          }))
        };
      });

      console.log('DEBUG: Actor-Portfolio Relationships:', actorPortfolioMap);
    }
  }, [actorUsers, portfolios, variationsMap]);

  // Create Voice Actor options for dropdown
  const voiceActorOptions = useMemo(() => {
    // Start with the "All Voice Actors" option
    const options = [{ key: 'all', text: 'All Voice Actors', value: 'all' }];

    // Add an option for each voice actor
    if (actorUsers && actorUsers.length > 0) {
      actorUsers.forEach(actor => {
        // Create a display name using available properties
        const displayName = actor.displayName ||
          (actor.firstName && actor.lastName ? `${actor.firstName} ${actor.lastName}` : null) ||
          actor.name ||
          actor.email ||
          `Actor ${actor.uuid.substring(0, 8)}`;

        options.push({
          key: actor.uuid,
          text: displayName,
          value: actor.uuid
        });
      });
    }

    return options;
  }, [actorUsers]);

  // Create Character options for dropdown
  const characterOptions = useMemo(() => {
    // Start with the "All Characters" option
    const options = [{ key: 'all', text: 'All Characters', value: 'all' }];

    // Create a Set to track unique character IDs we've already added
    const addedCharacters = new Set();

    // Add an option for each unique character
    if (characters && characters.length > 0) {
      characters.forEach(character => {
        if (character.uuid && !addedCharacters.has(character.uuid)) {
          addedCharacters.add(character.uuid);
          options.push({
            key: character.uuid,
            text: character.name || `Character ${character.uuid.substring(0, 8)}`,
            value: character.uuid
          });
        }
      });
    }

    return options;
  }, [characters]);

  // Define applyFilters with useCallback to avoid dependency loops
  const applyFilters = React.useCallback((status, usage, voiceActor, character) => {
    // Use effectivePortfolioData instead of portfolioData
    let filtered = Array.isArray(effectivePortfolioData) ? [...effectivePortfolioData] : [];

    // Filter by status
    if (status !== 'all') {
      filtered = filtered.filter(portfolio => portfolio.status === status);
    }

    // Filter by usage
    if (usage !== 'all') {
      filtered = filtered.filter(portfolio => portfolio.usage === usage);
    }

    // Filter by voice actor
    if (voiceActor !== 'all') {
      console.log(`Applying voice actor filter for ID: ${voiceActor}`);

      // Check if we can use the nested structure from the API
      if (actorUsers.length === 1 && actorUsers[0].uuid === voiceActor && actorUsers[0].portfolios) {
        // Use the nested structure directly - this is the orthogonal approach
        console.log(`Using nested structure for actor ${voiceActor}`);
        filtered = actorUsers[0].portfolios.map(portfolio => ({
          ...portfolio,
          // Ensure we have a deep copy to avoid modifying the original data
          characters: [...(portfolio.characters || [])]
        }));
        console.log(`Found ${filtered.length} portfolios using nested structure`);
      } else {
        // Fallback to the old approach if the nested structure is not available
        console.log(`Nested structure not available for actor ${voiceActor}, using fallback approach`);

        // Get the selected actor object
        const selectedActor = actorUsers.find(actor => actor.uuid === voiceActor);
        console.log('Selected actor:', selectedActor);

        if (selectedActor) {
          // Get all possible IDs for this actor
          const actorIds = [
            selectedActor.uuid,
            selectedActor.id,
            selectedActor._id,
            selectedActor.userId,
            selectedActor.user_id,
            selectedActor.email // Sometimes email is used as an identifier
          ].filter(Boolean); // Filter out undefined/null values

          console.log('Possible actor IDs to match:', actorIds);

          // Filter portfolios based on actorId property
          const actorPortfolios = filtered.filter(portfolio =>
            portfolio.actorId === voiceActor ||
            (portfolio.actorId && actorIds.includes(portfolio.actorId))
          );

          if (actorPortfolios.length > 0) {
            console.log(`Found ${actorPortfolios.length} portfolios with matching actorId`);
            filtered = actorPortfolios.map(portfolio => ({
              ...portfolio,
              characters: [...(portfolio.characters || [])]
            }));
          } else {
            // If no direct matches, use the demo filter as a last resort
            console.log('No portfolios found with matching actorId, using demo filter');
            const demoPortfolios = filtered.filter(portfolio => {
              return selectedActor.uuid === '0cc83cea-efbc-4ab9-b843-f2592a97db81' ?
                portfolio.name === 'Forcebottle stuff' :
                portfolio.name === 'Nevergate Characters';
            });

            filtered = demoPortfolios.map(portfolio => ({
              ...portfolio,
              characters: [...(portfolio.characters || [])]
            }));
          }
        } else {
          console.warn(`Actor with ID ${voiceActor} not found in actorUsers`);
          filtered = []; // No matching actor, so no portfolios
        }
      }

      // Log the filtered portfolios for debugging
      console.log(`Filtered portfolios for voice actor ${voiceActor}:`, filtered);
      console.log(`Total characters in filtered portfolios: ${filtered.reduce((sum, p) => sum + (p.characters?.length || 0), 0)}`);
    }

    // Filter by character
    if (character !== 'all') {
      console.log(`Applying character filter for ID: ${character}`);

      // First, find portfolios that contain the selected character
      const portfoliosWithCharacter = filtered.filter(portfolio =>
        portfolio.characters &&
        portfolio.characters.some(char => char.uuid === character)
      );

      console.log(`Found ${portfoliosWithCharacter.length} portfolios containing character ${character}`);

      // Then, create new portfolio objects with only the selected character
      filtered = portfoliosWithCharacter.map(portfolio => {
        // Find the selected character in this portfolio
        const selectedCharacter = portfolio.characters.find(char => char.uuid === character);

        // Create a new portfolio object with only the selected character
        return {
          ...portfolio,
          characters: selectedCharacter ? [selectedCharacter] : []
        };
      });

      console.log(`Filtered down to ${filtered.length} portfolios with character ${character} visible`);
    }

    setFilteredPortfolios(filtered);
  }, [effectivePortfolioData]);

  // Update filtered portfolios when data or filters change
  useEffect(() => {
    applyFilters(statusFilter, usageFilter, voiceActorFilter, characterFilter);
  }, [effectivePortfolioData, statusFilter, usageFilter, voiceActorFilter, characterFilter, applyFilters]);

  // Filter handlers
  const handleStatusFilter = (e, { value }) => {
    setStatusFilter(value);
    applyFilters(value, usageFilter, voiceActorFilter, characterFilter);
  };

  const handleUsageFilter = (e, { value }) => {
    setUsageFilter(value);
    applyFilters(statusFilter, value, voiceActorFilter, characterFilter);
  };

  const handleVoiceActorFilter = (e, { value }) => {
    setVoiceActorFilter(value);
    applyFilters(statusFilter, usageFilter, value, characterFilter);
  };

  const handleCharacterFilter = (e, { value }) => {
    setCharacterFilter(value);
    applyFilters(statusFilter, usageFilter, voiceActorFilter, value);
  };

  return (
    <>
      {/* Header Section */}
      <div className='listContainer' style={{ marginTop: "30px", marginBottom: "20px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src={characterImage} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">My Voice Lists</span>
              <span className="blgListSubTitle">My Character Recordings</span>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      {/* Provider Profile Section */}
      <div className='listContainer'>
        <ProviderProfile characters={characters} variationsMap={variationsMap} />
      </div>

      {/* My Recordings Heading */}
      <div className='listContainer'>
        <div className="subPageHeaderFont" style={{ marginBottom: '10px' }}>My Voice Portfolio</div>
      </div>

      {/* Filtering Options */}
      <div className='listContainer'>
        <Segment basic>
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by status'
                  fluid
                  selection
                  options={[
                    { key: 'all', text: 'All Statuses', value: 'all' },
                    { key: 'active', text: 'Active', value: 'Active' },
                    { key: 'in-development', text: 'In Development', value: 'In Development' },
                    { key: 'completed', text: 'Completed', value: 'Completed' },
                  ]}
                  onChange={handleStatusFilter}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by usage'
                  fluid
                  selection
                  options={[
                    { key: 'all', text: 'All Usage Types', value: 'all' },
                    { key: 'audiobook', text: 'Audiobook', value: 'Audiobook Series' },
                    { key: 'video-game', text: 'Video Game', value: 'Video Game' },
                    { key: 'animation', text: 'Animation', value: 'Animation Series' },
                    { key: 'documentary', text: 'Documentary', value: 'Documentary Series' },
                  ]}
                  onChange={handleUsageFilter}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by voice actor'
                  fluid
                  selection
                  options={voiceActorOptions}
                  onChange={handleVoiceActorFilter}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by character'
                  fluid
                  selection
                  options={characterOptions}
                  onChange={handleCharacterFilter}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} textAlign="right">
                <Button
                  color="blue"
                  icon
                  labelPosition="left"
                  onClick={() => {
                    setStatusFilter('all');
                    setUsageFilter('all');
                    setVoiceActorFilter('all');
                    setCharacterFilter('all');
                    applyFilters('all', 'all', 'all', 'all');
                  }}
                >
                  <Icon name="refresh" />
                  Reset Filters
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      </div>

      {/* Portfolio Recordings List */}
      <div className='listContainer'>
        {/* Loading State */}
        {isLoading && (
          <Segment>
            <Dimmer active inverted>
              <Loader inverted>Loading Portfolios</Loader>
            </Dimmer>
            <Image src='https://react.semantic-ui.com/images/wireframe/short-paragraph.png' />
          </Segment>
        )}

        {/* Error State */}
        {error && (
          <Message negative>
            <Message.Header>Error Loading Portfolios</Message.Header>
            <p>{error || 'An unknown error occurred'}</p>
          </Message>
        )}

        {/* Success State */}
        {!isLoading && !error && (
          <>
            {actorUsers.length === 0 ? (
              <Message warning>
                <Message.Header>No Voice Actors Found</Message.Header>
                <p>You need to create a voice actor before you can see portfolios.</p>
                <Button
                  primary
                  onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-ACTOR-PROFILE' } })}
                  style={{ marginTop: '10px' }}
                >
                  <Icon name='user plus' />
                  Create Voice Actor
                </Button>
              </Message>
            ) : filteredPortfolios.length === 0 ? (
              <Message info>
                <Message.Header>No Portfolios Found</Message.Header>
                <p>
                  {statusFilter === 'all' && usageFilter === 'all'
                    ? 'There are no portfolios in the system. Create portfolios in the Voice Actor Worksheet.'
                    : `There are no portfolios matching the selected filters. Try changing the filters.`
                  }
                </p>
                <Button
                  primary
                  onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-ACTOR-WORKSHEET' } })}
                  style={{ marginTop: '10px' }}
                >
                  <Icon name='plus' />
                  Go to Voice Actor Worksheet
                </Button>
              </Message>
            ) : (
              <Grid style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}>
                <Grid.Row>
                  <Grid.Column>
                    <Grid columns={2}>
                      {filteredPortfolios.map((portfolio, index) => (
                        <Grid.Column key={portfolio.uuid}>
                          <PortfolioRecordingItem
                            key={portfolio.uuid}
                            index={index}
                            portfolio={portfolio}
                            characters={portfolio.characters || []}
                            variationsMap={variationsMap}
                          />
                        </Grid.Column>
                      ))}
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default VoiceLabMyRecordings;
